import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';
import gsap from 'gsap';
import { STORES, type Store } from '~/common/constants/region';
import { toStore } from '~/common/utils/region';
import { regionModalOpenState, regionInfoState } from '~/recoil/useRegion';
import CloseModal from '~/components/icons/CloseModal';
import CountryFlag from '~/components/CountryFlag';
import { fontFamilies } from '~/theme/typography';

// Rule lives in app.css; keep the two names in step.
const SCROLL_LOCK_CLASS = 'scroll-locked';

interface IRegionSelectModalProps {
  onStoreChange: (store: Store) => void;
}

const RegionSelectModal = ({ onStoreChange }: IRegionSelectModalProps) => {
  const [isOpen, setIsOpen] = useRecoilState(regionModalOpenState);
  const { region, countryCode } = useRecoilValue(regionInfoState);
  const current = toStore(region, countryCode);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    gsap.fromTo(
      modalRef.current,
      { opacity: 0, scale: 0.96 },
      { opacity: 1, scale: 1, duration: 0.4, ease: 'power4.out' },
    );
  }, [isOpen]);

  // Lock page scroll while open with a class, never the inline style: the initial
  // loader and MobileMenu both write `body.style.overflow` on their own schedules
  // (the loader unlocks mid-way through a load-time prompt; the menu writes `auto`
  // in the same tick it hands over), so an inline lock gets overwritten and a
  // restored inline value goes stale. The class's `!important` rule (app.css) wins
  // over any inline value, and removing it leaves theirs untouched.
  useEffect(() => {
    if (!isOpen) return;

    document.body.classList.add(SCROLL_LOCK_CLASS);
    return () => document.body.classList.remove(SCROLL_LOCK_CLASS);
  }, [isOpen]);

  const closeModal = (onComplete?: () => void) => {
    if (!modalRef.current) return;

    gsap.to(modalRef.current, {
      opacity: 0,
      scale: 0.96,
      duration: 0.3,
      ease: 'power4.in',
      onComplete: () => {
        setIsOpen(false);
        onComplete?.();
      },
    });
  };

  if (!isOpen) return null;

  return (
    <Wrapper>
      <Backdrop />
      <Modal
        ref={modalRef}
        role="dialog"
        aria-labelledby="region-select-title"
        aria-modal="true"
      >
        <CloseButton
          type="button"
          onClick={() => closeModal()}
          aria-label="Close"
        >
          <CloseModal />
        </CloseButton>
        <Header>
          <Title id="region-select-title">SELECT YOUR STORE</Title>
        </Header>

        <StoreList>
          {Object.values(STORES).map((store) => (
            <li key={store.countryCode}>
              <StoreButton
                type="button"
                aria-pressed={store === current}
                onClick={() => closeModal(() => onStoreChange(store))}
              >
                <CountryFlag code={store.countryCode} />
                <StoreText>
                  <StoreName>{store.name}</StoreName>
                  <StoreDetails>{store.details}</StoreDetails>
                </StoreText>
              </StoreButton>
            </li>
          ))}
        </StoreList>
      </Modal>
    </Wrapper>
  );
};

export default RegionSelectModal;

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.regionSelectModal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
`;

const Modal = styled.div`
  pointer-events: auto;
  position: relative;
  width: 100%;
  max-width: 600px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.modalBackgroundBlur};
  backdrop-filter: blur(4px);
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.modal};
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  opacity: 0;
  transform: scale(0.96);
  will-change: opacity, transform;

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 20px 16px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 40px;
  font-style: normal;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin: 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 24px;
    padding: 0 24px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StoreList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// The current store reads as selected through the border alone, so it is also
// exposed as `aria-pressed`.
const StoreButton = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.white40};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.whiteAlpha10};
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-align: left;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.button};

  &:hover,
  &[aria-pressed='true'] {
    border-color: ${({ theme }) => theme.colors.white};
  }
`;

const StoreText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StoreName = styled.span`
  font-size: 16px;
  font-weight: 450;
`;

const StoreDetails = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.mutedOnDark};
`;
